"use client";

import { type PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";

type OccasionalWiggleProps = {
  className?: string;
  durationMs?: number; // how long one wiggle lasts
  minIntervalMs?: number; // minimum time between wiggles
  maxIntervalMs?: number; // maximum time between wiggles
  origin?: string; // CSS transform-origin (e.g., '50% 90%')
  intensity?: number; // rotation multiplier
};

export function OccasionalWiggle({
  children,
  className,
  durationMs = 900,
  minIntervalMs = 3000,
  maxIntervalMs = 7000,
  origin = "50% 75%",
  intensity = 1,
}: PropsWithChildren<OccasionalWiggleProps>) {
  const [wiggling, setWiggling] = useState(false);
  const timersRef = useRef<number[]>([]);
  const keyframesNameRef = useRef(`pm-wiggle-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    const schedule = () => {
      const span = maxIntervalMs - minIntervalMs;
      const wait = Math.floor(minIntervalMs + Math.random() * (span <= 0 ? 0 : span));
      const t1 = window.setTimeout(() => {
        setWiggling(true);
        const t2 = window.setTimeout(() => {
          setWiggling(false);
          schedule();
        }, durationMs);
        timersRef.current.push(t2);
      }, wait);
      timersRef.current.push(t1);
    };
    schedule();
    return () => {
      for (const t of timersRef.current) clearTimeout(t);
      timersRef.current = [];
    };
  }, [durationMs, minIntervalMs, maxIntervalMs]);

  const keyframesCss = useMemo(() => {
    // Slightly reduced amplitudes with faster damping
    const base = [0, 6, -5, 4, -3, 2, -1.2, 0.6, -0.3, 0.1, 0];
    const scaled = base.map((d) => +(d * intensity).toFixed(2));
    return `
      @keyframes ${keyframesNameRef.current} {
        /* Quick tilt in */
        0% { transform: rotate(${scaled[0]}deg); }
        12% { transform: rotate(${scaled[1]}deg); }
        /* Slight hold at peak */
        16% { transform: rotate(${scaled[1]}deg); }
        /* Ease out oscillations */
        30% { transform: rotate(${scaled[2]}deg); }
        42% { transform: rotate(${scaled[3]}deg); }
        54% { transform: rotate(${scaled[4]}deg); }
        66% { transform: rotate(${scaled[5]}deg); }
        78% { transform: rotate(${scaled[6]}deg); }
        88% { transform: rotate(${scaled[7]}deg); }
        96% { transform: rotate(${scaled[8]}deg); }
        98% { transform: rotate(${scaled[9]}deg); }
        100% { transform: rotate(${scaled[10]}deg); }
      }
    `;
  }, [intensity]);

  return (
    <span
      className={`inline-block ${className ?? ""}`}
      style={
        wiggling
          ? {
              animationName: keyframesNameRef.current,
              animationDuration: `${durationMs}ms`,
              animationTimingFunction: "cubic-bezier(0.2, 0.8, 0.2, 1)",
              animationFillMode: "both",
              transformOrigin: origin,
              display: "inline-block",
            }
          : { transformOrigin: origin, display: "inline-block" }
      }
    >
      {children}
      <style jsx global>
        {keyframesCss}
      </style>
    </span>
  );
}
